import {
  Component,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  computed,
  inject,
  input,
} from "@angular/core";
import { Router } from "@angular/router";
import { toPascalCase } from "@core/services/utils/functions/toPascalCase";
import { MessagingStateService } from "@core/services/utils/messaging-state.service";
import { UserStorageService } from "@core/services/utils/user-storage.service";
import { TranslateService } from "@ngx-translate/core";
import { BEBasicService } from "@services/be-basic.service";
import { BeUsersService } from "@services/business/user-services/be-users.service";
import { LCEventEnum } from "@services/ui-helpers/_models/interfaces/messaging.interfaces";
import {
  NotificationMessage,
  NotificationMessageReceiver,
} from "@services/ui-helpers/_models/interfaces/notifications.interfaces";
import { BENotificationsService } from "@services/ui-helpers/notifications-services/be-notifications.service";
import { Subject, takeUntil } from "rxjs";
import { RecordHeaderWrapperComponent } from "../../components/record-header-wrapper/record-header-wrapper.component";
import { LcHeaderMobileComponent } from "../lc-header-mobile/lc-header-mobile.component";
import { NotificationEvent } from "../lc-header/header-icons/notifications-panel/notification-item/types";

@Component({
  selector: "app-responsive-header",
  templateUrl: "./responsive-header.component.html",
  imports: [LcHeaderMobileComponent, RecordHeaderWrapperComponent],
})
export class ResponsiveHeaderComponent
  implements OnInit, OnChanges, OnDestroy, NotificationMessageReceiver
{
  private readonly usersService = inject(BeUsersService);
  private notificationsService = inject(BENotificationsService);
  private messagingService = inject(MessagingStateService);
  protected apiService = inject(BEBasicService);
  private userStorageService = inject(UserStorageService);
  private router = inject(Router);
  private readonly translateService = inject(TranslateService);

  public readonly isMobile = input(false);
  public readonly isUserLoggedIn = input(false);
  protected newMessages = false;
  protected notifications: NotificationEvent[] = [];

  protected readonly showMobileHeader = computed(
    () => this.isMobile() && this.isUserLoggedIn()
  );

  private readonly MAX_NOTIFICATIONS = 10;
  private readonly destroy$ = new Subject<void>();
  private notificationId = -1;
  private rolesInitialized = false;
  private roles: {
    isAdmin?: boolean;
    isLawyer?: boolean;
    isUser?: boolean;
    isReviewer?: boolean;
  };

  private readonly RECORD_NOTIFICATIONS = [
    LCEventEnum.NOTIF_RECORD_CREATED,
    LCEventEnum.NOTIF_RECORD_PAYMENT_RECEIVED,
    LCEventEnum.NOTIF_RECORD_DOCUMENT_UPLOADED,
    LCEventEnum.NOTIF_RECORD_ACTIVITY_UPDATED,
    LCEventEnum.NOTIF_RECORD_CLOSED,
    LCEventEnum.NOTIF_RECORD_PAYMENT_REQUESTED,
    LCEventEnum.NOTIF_RECORD_DOCUMENT_REJECTED,
    LCEventEnum.NOTIF_RECORD_DOCUMENT_VALIDATED,
  ];

  ngOnChanges(changes: SimpleChanges): void {
    const isUserLoggedInChange = changes["isUserLoggedIn"];

    if (!isUserLoggedInChange) {
      return;
    }

    this.initRoles(this.isUserLoggedIn());
  }

  ngOnInit(): void {
    this.initRoles(this.isUserLoggedIn());

    this.setupMessageSubscription();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.notificationId !== -1) {
      this.notificationsService.unsubscribeToNotifications(this.notificationId);
    }
  }

  private async initRoles(isUserLoggedIn: boolean): Promise<void> {
    if (!isUserLoggedIn || this.rolesInitialized) {
      return;
    }
    this.rolesInitialized = true;
    try {
      await this.usersService.loadRoles();
      this.roles = this.usersService.getRoleFlags();
      this.setupNotificationSubscription();
    } catch {
      this.rolesInitialized = false;
    }
  }

  private setupMessageSubscription(): void {
    this.messagingService.newMessages$
      .pipe(takeUntil(this.destroy$))
      .subscribe(hasNewMessages => {
        this.newMessages = hasNewMessages;
      });
  }

  private setupNotificationSubscription(): void {
    this.notificationId = this.notificationsService.subscribeToNotifications(
      this,
      [LCEventEnum.USER_ONBOARDED, ...this.RECORD_NOTIFICATIONS],
      false
    );
  }

  addNotification(notification: NotificationMessage): void {
    if (!notification?.id || !notification?.event) {
      return;
    }

    if (this.notifications.some(n => n.id === notification.id)) {
      return;
    }

    const notificationEvent: NotificationEvent = {
      id: notification.id,
      subject:
        notification.subject ??
        this.translateService.instant(
          `Notifications.${this.roles?.isReviewer ? "Reviewer" : "User"}.${toPascalCase(notification.event)}`
        ),
      body: notification.body ?? "",
      date: new Date().toLocaleString(),
      readed: false,
      type: notification.event,
      extra: notification,
    };

    this.notifications.unshift(notificationEvent);

    if (this.notifications.length > this.MAX_NOTIFICATIONS) {
      this.notifications = this.notifications.slice(0, this.MAX_NOTIFICATIONS);
    }
  }

  processNotification(notification: NotificationMessage): void {
    if (!notification?.event) {
      return;
    }

    if (
      this.roles.isAdmin &&
      notification.event === LCEventEnum.USER_ONBOARDED
    ) {
      this.addNotification(notification);
    }

    if (
      this.roles.isReviewer &&
      [
        LCEventEnum.NOTIF_RECORD_CREATED,
        LCEventEnum.NOTIF_RECORD_PAYMENT_RECEIVED,
        LCEventEnum.NOTIF_RECORD_CLOSED,
      ].includes(notification.event)
    ) {
      this.addNotification(notification);
    } else if (
      this.roles.isLawyer &&
      [
        LCEventEnum.NOTIF_RECORD_DOCUMENT_UPLOADED,
        LCEventEnum.NOTIF_RECORD_ACTIVITY_UPDATED,
      ].includes(notification.event)
    ) {
      this.addNotification(notification);
    } else if (
      this.roles.isUser &&
      [
        LCEventEnum.NOTIF_RECORD_PAYMENT_REQUESTED,
        LCEventEnum.NOTIF_RECORD_DOCUMENT_REJECTED,
        LCEventEnum.NOTIF_RECORD_DOCUMENT_VALIDATED,
      ].includes(notification.event)
    ) {
      this.addNotification(notification);
    }
  }

  protected get hasNotifications(): boolean {
    return this.notifications?.length > 0;
  }

  protected get userName(): string {
    return this.getName(
      this.userStorageService.registeredUser
        ? this.userStorageService.registeredUser.name
        : "-"
    );
  }

  protected get userSurname(): string {
    return this.getName(
      this.userStorageService.registeredUser
        ? this.userStorageService.registeredUser.familyName
        : "-"
    );
  }

  private getName(name: string | undefined | null): string {
    if (name === undefined || name === null || name.length < 1) {
      return "-";
    }
    return name;
  }

  markNotificationAsRead(notification: NotificationEvent): void {
    this.notifications = this.notifications.filter(
      n => n.id !== notification.id
    );
    this.doNotificationAction(notification);
  }

  doNotificationAction(notification: NotificationEvent): void {
    if (this.RECORD_NOTIFICATIONS.includes(notification.type)) {
      const notificationMessage = notification.extra as NotificationMessage;

      if (!notificationMessage?.properties?.["recordId"]) {
        return;
      }

      const recordId = notificationMessage?.properties?.["recordId"];

      if (this.roles.isReviewer) {
        if (
          notification.type === LCEventEnum.NOTIF_RECORD_CREATED ||
          notification.type === LCEventEnum.NOTIF_RECORD_CLOSED
        ) {
          this.router.navigate([`/dashboard/operative/records/${recordId}`]);
        } else if (
          notification.type === LCEventEnum.NOTIF_RECORD_DOCUMENT_UPLOADED
        ) {
          this.router.navigate([`/dashboard/operative/records/${recordId}`], {
            fragment: "documents",
          });
        } else if (
          notification.type === LCEventEnum.NOTIF_RECORD_PAYMENT_RECEIVED
        ) {
          this.router.navigate([`/dashboard/operative/records/${recordId}`], {
            fragment: "payments",
          });
        } else if (
          notification.type === LCEventEnum.NOTIF_RECORD_ACTIVITY_UPDATED
        ) {
          this.router.navigate([`/dashboard/operative/records/${recordId}`], {
            fragment: "activities",
          });
        }
      } else if (this.roles.isUser) {
        if (
          [
            LCEventEnum.NOTIF_RECORD_PAYMENT_RECEIVED,
            LCEventEnum.NOTIF_RECORD_PAYMENT_REQUESTED,
          ].includes(notification.type)
        ) {
          this.router.navigate([`/records/${recordId}/payments`]);
        } else if (
          [
            LCEventEnum.NOTIF_RECORD_DOCUMENT_REJECTED,
            LCEventEnum.NOTIF_RECORD_DOCUMENT_VALIDATED,
          ].includes(notification.type)
        ) {
          this.router.navigate([`/records/${recordId}/documentation`]);
        }
      }
    }
  }
}
